/**
 * The word to type, floating above the next platform.
 *
 * Target word on top at 56px (CLAUDE.md floor is 48px logical), the letters the
 * player has typed beneath it in green or red, and a blinking caret. Letter
 * objects are pooled and updated, never recreated per keystroke.
 */
import Phaser from 'phaser';
import { ensurePlaceholderTexture } from '../../art/placeholders';
import { artScale } from '../../art/artScale';
import { letterStates, wrongPositions } from '../../systems/TypingInput';
import type { Match } from '../../systems/TypingInput';
import { FONT_FAMILY, PALETTE } from '../../types';

export type PanelTone = 'normal' | 'spring' | 'jetpack';

export const WORD_PANEL_DEPTH = 30;

const MAX_LETTERS = 24;
const WORD_SIZE = 56;
const TYPED_SIZE = 40;
const PAD_X = 34;
const PAD_Y = 18;

/**
 * Clear air between the word and what has been typed.
 *
 * The two rows are stacked and centred on the panel's origin so the gap is a
 * stated distance rather than a by-product of each row's line box. Text is
 * taller than its font size - a 56px word's box runs about 72px, and its
 * descenders and the caret both reach well past the number - so positioning
 * the second row by eye off the first leaves them touching.
 */
const ROW_GAP = 18;
const CONTENT_H = WORD_SIZE + ROW_GAP + TYPED_SIZE;
const WORD_Y = -CONTENT_H / 2 + WORD_SIZE / 2;
const TYPED_Y = CONTENT_H / 2 - TYPED_SIZE / 2;

const GREEN = '#3ddc84';
const RED = '#ff6b6b';

/** Spring is teal and jetpack orange (CLAUDE.md); Prompt 5 turns these on. */
const TONES: Readonly<Record<PanelTone, { border: number; glow: number }>> = {
  normal: { border: 0x8ecae6, glow: 0x0b1220 },
  jetpack: { border: 0xff8c42, glow: 0x3a1c0a },
  spring: { border: 0x2ec4b6, glow: 0x08302c },
};

export class WordPanel extends Phaser.GameObjects.Container {
  private readonly glow: Phaser.GameObjects.Graphics;
  private readonly background: Phaser.GameObjects.Graphics;
  private readonly icon: Phaser.GameObjects.Image;
  private pulseTween: Phaser.Tweens.Tween | undefined;
  private readonly wordText: Phaser.GameObjects.Text;
  private readonly letters: Phaser.GameObjects.Text[] = [];
  private readonly caret: Phaser.GameObjects.Rectangle;
  private caretTween?: Phaser.Tweens.Tween;
  private target = '';
  private tone: PanelTone = 'normal';
  private correcting = false;
  private correctionTimer: Phaser.Time.TimerEvent | undefined;
  private match: Match = 'strict';

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);

    this.glow = scene.add.graphics();
    this.background = scene.add.graphics();
    ensurePlaceholderTexture(scene, 'powerups/spring_icon');
    ensurePlaceholderTexture(scene, 'powerups/jetpack_icon');
    this.icon = scene.add
      .image(0, 0, 'powerups/spring_icon')
      .setOrigin(0.5, 0.5)
      .setScale(0.62 * artScale(scene, 'powerups/spring_icon'))
      .setVisible(false);
    this.wordText = scene.add
      .text(0, WORD_Y, '', {
        fontFamily: FONT_FAMILY,
        fontSize: `${WORD_SIZE}px`,
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 7,
      })
      .setOrigin(0.5, 0.5);

    this.caret = scene.add.rectangle(0, TYPED_Y, 4, TYPED_SIZE, 0xffc145);

    this.add([this.glow, this.background, this.icon, this.wordText, this.caret]);

    for (let i = 0; i < MAX_LETTERS; i += 1) {
      const letter = scene.add
        .text(0, TYPED_Y, '', {
          fontFamily: FONT_FAMILY,
          fontSize: `${TYPED_SIZE}px`,
          color: GREEN,
          stroke: PALETTE.ink,
          strokeThickness: 5,
        })
        .setOrigin(0.5, 0.5)
        .setVisible(false);
      this.letters.push(letter);
      this.add(letter);
    }

    this.setDepth(WORD_PANEL_DEPTH);
    scene.add.existing(this);
    this.startCaretBlink();
  }

  /** Point the panel at a new word and clear what was typed. */
  setWord(word: string, tone: PanelTone = 'normal'): void {
    this.target = word;
    this.tone = tone;
    this.wordText.setText(word).setColor(PALETTE.snow);
    this.applyTone();
    this.setTyped('');
    this.redraw();
  }

  /** Bonus panels carry their power-up icon and a slow pulse (CLAUDE.md). */
  private applyTone(): void {
    this.stopPulse();

    if (this.tone === 'normal') {
      this.icon.setVisible(false);
      this.glow.setAlpha(0);
      this.setScale(1);
      return;
    }

    const key = this.tone === 'spring' ? 'powerups/spring_icon' : 'powerups/jetpack_icon';
    ensurePlaceholderTexture(this.scene, key);
    this.icon
      .setTexture(key)
      .setVisible(true)
      .setAlpha(1)
      .setScale(0.62 * artScale(this.scene, key));

    this.glow.setAlpha(0.45);
    this.pulseTween = this.scene.tweens.add({
      targets: [this.glow, this.icon],
      alpha: { from: 0.45, to: 1 },
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Icon puffs out of existence after a missed bonus word. */
  fizzleIcon(): { x: number; y: number; tint: number } | null {
    if (!this.icon.visible) return null;
    const world = { x: this.x + this.icon.x, y: this.y + this.icon.y };
    const tint = TONES[this.tone].border;
    this.stopPulse();
    this.scene.tweens.add({
      targets: this.icon,
      alpha: 0,
      scale: 0.2,
      angle: 160,
      duration: 260,
      onComplete: () =>
        this.icon
          .setVisible(false)
          .setAngle(0)
          .setScale(0.62 * artScale(this.scene, this.icon.texture.key)),
    });
    this.scene.tweens.add({ targets: this.glow, alpha: 0, duration: 260 });
    return { ...world, tint };
  }

  private stopPulse(): void {
    this.pulseTween?.remove();
    this.pulseTween = undefined;
    this.icon.setAlpha(1);
  }

  /** Re-render the typed row for the current buffer. */
  setTyped(typed: string): void {
    if (this.correcting && typed !== '') this.clearCorrection();
    const states = letterStates(typed, this.target, this.match);
    const shown = Math.min(typed.length, MAX_LETTERS);

    // Lay the typed letters out centred, using each glyph's measured width.
    const widths: number[] = [];
    for (let i = 0; i < shown; i += 1) {
      const letter = this.letters[i];
      if (letter === undefined) continue;
      letter.setText(typed[i] ?? '');
      letter.setColor(states[i] === 'correct' ? GREEN : RED);
      letter.setVisible(true);
      widths.push(letter.width);
    }
    for (let i = shown; i < MAX_LETTERS; i += 1) {
      this.letters[i]?.setVisible(false);
    }

    const total = widths.reduce((sum, w) => sum + w, 0);
    let cursor = -total / 2;
    for (let i = 0; i < shown; i += 1) {
      const letter = this.letters[i];
      const width = widths[i] ?? 0;
      if (letter !== undefined) letter.setX(cursor + width / 2);
      cursor += width;
    }

    this.caret.setX(cursor + 4);
    this.redraw();
  }

  /**
   * Flash the correct spelling with the mistyped letters in red. Non-blocking:
   * the player can start retyping straight away, and the first keystroke clears
   * it (CLAUDE.md: show it briefly, never punish).
   */
  flashCorrection(typed: string, durationMs: number, word: string = this.target): void {
    // `word` is the one that was missed, which after a drop is not the panel's
    // own target: the animal has fallen a platform and the next word is the one
    // below. Showing the target here would correct a word the child never got
    // wrong, against letters they typed for a different word.
    const wrong = new Set(wrongPositions(typed, word, this.match));
    this.wordText.setText(word).setColor(PALETTE.gold);
    this.correcting = true;

    const chars = [...word];
    const widths: number[] = [];
    for (let i = 0; i < Math.min(chars.length, MAX_LETTERS); i += 1) {
      const letter = this.letters[i];
      if (letter === undefined) continue;
      letter.setText(chars[i] ?? '');
      letter.setColor(wrong.has(i) ? RED : GREEN);
      letter.setVisible(true);
      widths.push(letter.width);
    }
    for (let i = chars.length; i < MAX_LETTERS; i += 1) {
      this.letters[i]?.setVisible(false);
    }
    const total = widths.reduce((sum, w) => sum + w, 0);
    let cursor = -total / 2;
    for (let i = 0; i < widths.length; i += 1) {
      const width = widths[i] ?? 0;
      this.letters[i]?.setX(cursor + width / 2);
      cursor += width;
    }

    this.caret.setVisible(false);
    this.redraw();

    this.correctionTimer?.remove();
    this.correctionTimer = this.scene.time.delayedCall(durationMs, () => this.clearCorrection());
  }

  /** Drops the correction display, whether it timed out or was typed through. */
  clearCorrection(): void {
    if (!this.correcting) return;
    this.correcting = false;
    this.correctionTimer?.remove();
    this.correctionTimer = undefined;
    // Back to the word actually up next, which may differ from the one shown.
    this.wordText.setText(this.target).setColor(PALETTE.snow);
    this.redraw();
    this.caret.setVisible(true);
    this.setTyped('');
  }

  /** Half the panel's current width, for anything that must keep clear of it. */
  get halfWidth(): number {
    return (Math.max(this.wordText.width, 160) + PAD_X * 2) / 2;
  }

  /** Named placeAt because Container already owns `moveTo`. */
  placeAt(x: number, y: number): void {
    this.setPosition(x, y);
  }

  /** Lenient on early sentence levels: a lowercase letter counts for a capital. */
  setMatch(match: Match): void {
    this.match = match;
  }

  override destroy(fromScene?: boolean): void {
    this.correctionTimer?.remove();
    this.pulseTween?.remove();
    this.caretTween?.remove();
    super.destroy(fromScene);
  }

  private redraw(): void {
    const width = Math.max(this.wordText.width, 160) + PAD_X * 2;
    const height = CONTENT_H + PAD_Y * 2;
    const top = -CONTENT_H / 2 - PAD_Y;
    const radius = 22;
    const { border, glow } = TONES[this.tone];

    this.background.clear();
    this.background.fillStyle(glow, 0.82);
    this.background.fillRoundedRect(-width / 2, top, width, height, radius);
    this.background.lineStyle(5, border, 1);
    this.background.strokeRoundedRect(-width / 2, top, width, height, radius);

    // Halo behind the panel, pulsed by applyTone for bonus words.
    this.glow.clear();
    if (this.tone !== 'normal') {
      for (const spread of [16, 9, 3]) {
        this.glow.lineStyle(spread, border, 0.16);
        this.glow.strokeRoundedRect(
          -width / 2 - spread / 2,
          top - spread / 2,
          width + spread,
          height + spread,
          radius + spread / 2,
        );
      }
    }

    // Icon sits just outside the panel, on the side with more room on stage.
    this.icon.setPosition(-width / 2 - 44, top + height / 2);
  }

  private startCaretBlink(): void {
    this.caretTween = this.scene.tweens.add({
      targets: this.caret,
      alpha: { from: 1, to: 0.1 },
      duration: 480,
      yoyo: true,
      repeat: -1,
    });
  }
}
